import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, Gift, Users, MessageCircle, Mail } from "lucide-react";

export default function ReferralCard({ referral }) {
  const [copied, setCopied] = useState(false);
  if (!referral) return null;

  const link = `${window.location.origin}/join?ref=${referral.referral_code}`;
  const message = `Join me on Zola Rewards — earn points on every flight & hotel booked with Zola Travels. Use my referral code ${referral.referral_code} and we both get ${referral.bonus_points} bonus points on your first booking! ${link}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — long-press the link to copy manually.");
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Zola Rewards",
          text: message,
          url: link,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copy();
    }
  };

  const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const em = `mailto:?subject=${encodeURIComponent("Join me on Zola Rewards")}&body=${encodeURIComponent(message)}`;

  return (
    <div className="w-card w-card-bordered p-5 sm:p-6" data-testid="referral-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl grid place-items-center"
               style={{ background: "rgba(201,162,75,0.14)", color: "var(--gold)" }}>
            <Gift className="h-5 w-5" />
          </div>
          <div>
            <div className="label-caps gold">Refer & earn</div>
            <div className="font-semibold text-base leading-tight">
              Invite a friend, earn {referral.bonus_points} points each
            </div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full"
             style={{
               background: "var(--bg-soft)",
               border: "1px solid var(--border)",
               color: "var(--text-muted)",
             }}
             data-testid="referral-count">
          <Users className="h-3.5 w-3.5" />
          <span className="font-semibold" style={{ color: "var(--text)" }}>
            {referral.total_referred}
          </span>
          referred
        </div>
      </div>

      <div className="rounded-xl p-3 flex items-center justify-between gap-3"
           style={{
             background: "var(--bg-soft)",
             border: "1px solid var(--border)",
           }}
           data-testid="referral-code-row">
        <div className="min-w-0 flex-1">
          <div className="label-caps">Your referral code</div>
          <div className="mt-0.5 font-mono text-lg font-semibold tracking-widest"
               data-testid="referral-code-value">
            {referral.referral_code}
          </div>
        </div>
        <button
          type="button"
          onClick={copy}
          data-testid="referral-copy-btn"
          className="h-9 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5"
          style={{
            background: copied ? "var(--gold)" : "var(--navy)",
            color: copied ? "#0b2447" : "#fff",
          }}
        >
          <Copy className="h-3.5 w-3.5" />
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <a
          href={wa}
          target="_blank"
          rel="noreferrer"
          data-testid="referral-whatsapp"
          className="h-10 rounded-lg inline-flex items-center justify-center gap-2 text-sm font-medium"
          style={{ background: "var(--bg-soft)", border: "1px solid var(--border)", color: "var(--text)" }}
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
        <a
          href={em}
          data-testid="referral-email"
          className="h-10 rounded-lg inline-flex items-center justify-center gap-2 text-sm font-medium"
          style={{ background: "var(--bg-soft)", border: "1px solid var(--border)", color: "var(--text)" }}
        >
          <Mail className="h-4 w-4" /> Email
        </a>
        <button
          type="button"
          onClick={shareNative}
          data-testid="referral-share"
          className="h-10 rounded-lg inline-flex items-center justify-center gap-2 text-sm font-medium"
          style={{ background: "var(--bg-soft)", border: "1px solid var(--border)", color: "var(--text)" }}
        >
          <Share2 className="h-4 w-4" /> Share
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-center">
        <div className="rounded-lg p-3"
             style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }}>
          <div className="label-caps">Friends joined</div>
          <div className="num-big mt-1">{referral.total_referred}</div>
        </div>
        <div className="rounded-lg p-3"
             style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }}>
          <div className="label-caps">Bonus points earned</div>
          <div className="num-big gold mt-1">
            {(referral.total_referral_points_earned || 0).toLocaleString()}
          </div>
        </div>
      </div>

      <p className="mt-3 text-[11px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
        Your friend must complete their first booking with Zola Travels for
        both of you to receive the bonus points. Points appear on your
        ledger automatically.
      </p>
    </div>
  );
}
